import * as Sentry from '@sentry/browser'

export default function mutationMixin(superclass) {
  return class CustomisedProduct extends superclass {
    setChoice(option, forcePrimary = false) {
      if (typeof option === 'string') {
        throw new Error('setChoice now expects an option object, not a string')
      }

      // Get part from option - we can't link this in the instance themselves as a single option might be available to multiple parts
      // (eg renegades lenses used in tempests)
      const part = this.product.getPartBySKU(option.sku)

      // If we're in parts mode then check this is the active part
      if (['parts', 'conversionkit'].includes(this.product?.pageMode)) this.setActivePart(part)

      // If we need to force this choice to primary then check
      // we're not doubling up on a swappable part
      const swappableEntry = this.product.swappableParts.find((swappable) =>
        swappable.includes(part.stockManagedSKUPrefix)
      )
      const swappableWithSKUPrefix = swappableEntry?.find(
        (partSKUPrefix) => partSKUPrefix !== part.stockManagedSKUPrefix
      )
      const swappableWithPart = this.product.parts.find(
        (part) => part.stockManagedSKUPrefix === swappableWithSKUPrefix
      )
      let currentlySecondary =
        part.id in this.state.secondaryChoices ||
        (swappableWithPart?.id && swappableWithPart.id in this.state.choices)

      if (currentlySecondary && forcePrimary) {
        this.swapPrimaryAndSecondaryChoices(swappableWithPart, part)

        // This is no longer a secondary choice
        currentlySecondary = false
      }

      if (currentlySecondary) {
        this.state.secondaryChoices = {
          ...this.state.secondaryChoices,
          [part.id]: option.id,
        }
      } else {
        this.state.choices = {
          ...this.state.choices,
          [part.id]: option.id,
        }
      }

      this.setFollowerChoices(part, option)
      this.checkSwappableParts()
      this.checkPartsMode()
    }

    setChoicesFromSKU(sku, ignoreLensTech = false, forcePrimary = false) {
      const allOptions = this.product.allOptions
      let skuWithLensTech = ''

      const options = sku
        .split('-')
        .slice(1)
        .map((optionSKU) => allOptions[optionSKU])
        .filter((option) => option)

      let hasFailedOverLensTech = false

      options.forEach((option) => {
        if (option.partType !== 'lenses') {
          return this.setChoice(option, forcePrimary)
        }

        // Get an array of available lenses for this product
        // which match the requested colour
        const availableLenses = this.lensPart.options.filter(
          (lens) => lens.skuWithoutLensTech === option.skuWithoutLensTech
        )

        if (/(_STRX|_STPRX|_THRX|_THPRX|_RX)/.test(option.sku) && this.isPrescriptionSunglasses) {
          skuWithLensTech = option.sku
        } else if (this.lensTechSelected && !ignoreLensTech) {
          skuWithLensTech = option.getSKUWithLensTech(this.lensTech)
        } else {
          skuWithLensTech = option.sku
        }

        const lensOption = availableLenses.find((lens) => lens.sku === skuWithLensTech)
        if (lensOption) {
          // If the choice is out of stock and not on pre-order then treat that a failover
          if (!lensOption.isInStock && !lensOption.isPreOrder) {
            hasFailedOverLensTech = true
          }

          return this.setChoice(lensOption)
        }

        // If not failover to the first available lens tech and flag that we've failed over
        const firstAvailableLens = availableLenses[0]
        if (firstAvailableLens) {
          hasFailedOverLensTech = true
          return this.setChoice(firstAvailableLens)
        }
      })

      return {
        hasFailedOverLensTech,
      }
    }

    selectPrebuiltVariant(prebuiltVariant) {
      if (!prebuiltVariant) {
        this.setProductCustomisedState(true)
        return
      }

      // Mark that this has not been customised
      this.setProductCustomisedState(false)

      // Reset existing choices so prebuilt assignment doesn't depend on prior state.
      this.state.choices = {}
      this.state.secondaryChoices = {}

      // Set choices
      prebuiltVariant.parts.forEach((part) => {
        // The part here comes from Shopify and is not a full PartOption instance
        const option = this.product.getOptionBySKU(part.sku)
        this.setChoice(option, true)
      })

      // Set secondary choices
      prebuiltVariant.secondaryParts?.forEach((part) => {
        const option = this.product.getOptionBySKU(part.sku)
        const partDetails = this.product.getPartBySKU(part.sku)
        if (!option || !partDetails?.id) return

        this.state.secondaryChoices = {
          ...this.state.secondaryChoices,
          [partDetails.id]: option.id,
        }
      })

      this.checkSwappableParts()
      this.checkPartsMode()

      if (prebuiltVariant.secondaryParts?.length) {
        this.state.conversionKitEnabled = true
      }

      if (!prebuiltVariant.secondaryParts?.length) {
        this.state.conversionKitEnabled = false
      }

      this.state.isPrebuiltVariant = true
      this.state.prebuiltVariant = prebuiltVariant
    }

    selectConfigurableProductVariant(variant) {
      this.setProductCustomisedState(false)

      this.state.choices = {}
      this.state.secondaryChoices = {}
      this.state.configurableProductVariant = variant
      this.state.isPrebuiltVariant = false
      this.state.prebuiltVariant = null
    }

    setChoicesFromPrebuiltOrVariantSKU(prebuiltSKU) {
      // Find the prebuilt variant
      const prebuiltVariant = this.product.prebuiltVariants.find((variant) => variant.sku === prebuiltSKU)
      // If set to a prebuilt variant then select it
      if (prebuiltVariant) return this.selectPrebuiltVariant(prebuiltVariant)

      // If set to a product variant then select it
      const productVariant = this.product.productVariants.find((variant) => variant.sku === prebuiltSKU)

      if (productVariant) {
        Object.values(productVariant.choices).forEach((choice) => this.setChoice(choice))
        return
      }
    }

    setFollowerChoices(leaderPart, leaderOption) {
      // Find a matching follower part
      const followerPart = this.product.parts.find((part) => part.followerOf === leaderPart.skuPrefix)
      if (!followerPart) return

      let leaderSKUSuffixOverride = leaderOption.leaderSKUOverride?.split('_').slice(1).join('_')

      const leaderSKUSuffix = leaderOption.sku.split('_').slice(1).join('_')
      let choice = followerPart.options.find((option) =>
        option.sku.endsWith(`_${leaderSKUSuffixOverride || leaderSKUSuffix}`)
      )

      // If we haven't been able to find a matching choice then check across infinite and non-infinite
      if (!choice) {
        const potentialSKUSuffixes = ['F', 'I', 'M'].map((char) => leaderSKUSuffix.replace(/^[FIM]/, char))
        choice = followerPart.options.find((option) => potentialSKUSuffixes.includes(option.skuWithoutPrefix))
      }

      // If we still haven't found a choice then track an error and return
      if (!choice) {
        Sentry.captureException(new Error(`No follower choice found for ${leaderOption.sku}`))
        console.error(`No follower choice found for ${leaderOption.sku}`)
        return
      }

      /**
       * Prefer non-infinite products until they sell out
       */
      if (choice.isRecycled) {
        const nonInfAlternative = followerPart.getNonInfiniteAlternative(choice)
        if (nonInfAlternative && nonInfAlternative.isInStock) {
          choice = nonInfAlternative
        }
      } else if (choice.isNonRecycled && !choice.isInStock) {
        const infAlternative = followerPart.getInfiniteAlternative(choice)
        if (infAlternative && infAlternative.isInStock) {
          choice = infAlternative
        }
      }

      this.setChoice(choice)
    }

    setDefaultChoices(queryParams) {
      this.state.choices = {}
      this.state.secondaryChoices = {}

      // Get default SKU from hero designs or query params
      const initialSKU =
        queryParams?.pbsku || queryParams?.id || queryParams?.sku || this.heroDesigns?.[0]?.sku
      const initialSecondarySKU = queryParams?.ckid || queryParams?.ckSKU

      // If this is a product variant then we need to set the choices from the product variant
      const matchingProductVariant = this.product.productVariants.find(
        (variant) => variant.sku === initialSKU
      )
      if (matchingProductVariant) {
        Object.values(matchingProductVariant.choices).forEach((choice) => this.setChoice(choice))
        return
      }

      // Check if this is a prebuilt variant
      const prebuiltDesign =
        initialSKU &&
        this.product.prebuiltVariants.find((variant) => {
          // Check if the SKU is the same, typically this will be _hero skus
          if (variant.sku === initialSKU) return true

          // Check if all SKU parts match
          const variantSKUParts = variant.customSKU.split('-').slice(1)
          const initialSKUParts = initialSKU.split('-').slice(1)
          if (variantSKUParts.length !== initialSKUParts.length) return false

          if (variantSKUParts.every((part) => initialSKUParts.includes(part))) return true

          // Check if all legacy SKU parts match
          const variantLegacySKUParts = variant.legacySKU.split('-').slice(1)
          if (variantLegacySKUParts.length !== initialSKUParts.length) return false
          if (variantLegacySKUParts.every((part) => initialSKUParts.includes(part))) return true

          return false
        })
      let isPrebuiltVariant = !!prebuiltDesign

      // Set choices from queryParams
      if (isPrebuiltVariant) {
        this.selectPrebuiltVariant(prebuiltDesign)
      } else {
        if (initialSKU) {
          this.state.choices = this.getChoicesFromSKU(initialSKU)
        }
        if (initialSecondarySKU) {
          this.state.secondaryChoices = this.getChoicesFromSKU(initialSecondarySKU)
          this.state.conversionKitEnabled = true
        }

        // Remove any choices that are invalid
        Object.entries(this.choices).forEach(([partId, optionId]) => {
          if (!optionId) delete this.state.choices[partId]
        })
        Object.entries(this.secondaryChoices).forEach(([partId, optionId]) => {
          if (!optionId) delete this.state.secondaryChoices[partId]
        })

        const initChoices = { ...this.state.choices }

        this.product.parts.forEach((part) => {
          // If this part is already defined then skip it
          if (part.id in this.state.choices || part.id in this.state.secondaryChoices) {
            return
          }

          // Identify if this is a swappable part and if so what index it is
          const swappablePartDetails = this.product.swappableParts.find((swappable) =>
            swappable.includes(part.stockManagedSKUPrefix)
          )
          const swappableIndex = swappablePartDetails?.findIndex(
            (skuPrefix) => skuPrefix === part.stockManagedSKUPrefix
          )
          const swappableWith = swappablePartDetails?.find(
            (skuPrefix) => skuPrefix !== part.stockManagedSKUPrefix
          )
          const swappableWithPart = this.product.parts.find(
            (part) => part.stockManagedSKUPrefix === swappableWith
          )
          const swappableInitDefined = swappableWith && swappableWithPart.id in initChoices

          if (
            swappableInitDefined ||
            swappableIndex === 1 ||
            (['parts', 'conversionkit'].includes(this.product?.pageMode) &&
              Object.keys(this.state.choices).length >= 1)
          ) {
            // If:
            //   this is a secondary swappable part
            //   OR we're in parts mode
            //   OR the alternative was defined in initial choices
            // Then store in secondaryChoices
            this.state.secondaryChoices[part.id] = part.options[0].id
          } else {
            this.state.choices[part.id] = part.options[0].id
          }
        })

        this.ensureChoiceValidity()
      }

      this.state.defaultChoicesSet = true
    }

    getChoicesFromSKU(sku) {
      if (!this.product) return {}
      const optionSKUs = sku.split('-').slice(1)
      const choices = Object.fromEntries(
        optionSKUs
          .map((optionSKU) => {
            const part = this.product.getPartBySKU(optionSKU)
            if (!part) return null

            let choice = this.product.getOptionBySKU(optionSKU)

            if (!choice) {
              return [part.id, part.options[0].id]
            }

            /**
             * Prefer non-infinite products until they sell out
             */
            if (choice.isRecycled) {
              const nonInfAlternative = part.getNonInfiniteAlternative(choice)
              if (nonInfAlternative && nonInfAlternative.isInStock) {
                choice = nonInfAlternative
              }
            } else if (choice.isNonRecycled && !choice.isInStock) {
              const infAlternative = part.getInfiniteAlternative(choice)
              if (infAlternative && infAlternative.isInStock) {
                choice = infAlternative
              }
            }

            return [part.id, choice.id]
          })
          .filter((choice) => choice)
      )

      return choices
    }

    ensureChoiceValidity() {
      // Check that no swappable parts are double-defined
      this.checkSwappableParts()

      // Check that no choices are undefined
      this.checkAllChoicesDefined()

      // Check that nose pieces match frame choice in custom mode
      this.checkNosePieceMatchesFrame()

      // Check only one part is defined in parts mode
      this.checkPartsMode()
    }

    checkNosePieceMatchesFrame() {
      // Only check in custom mode
      if (this.product?.pageMode !== 'custom') return

      // Find the nose piece part, return early if it doesn't exist on this product
      const nosePiecePart = this.product.parts.find((part) => part.type === 'nose_piece')
      if (!nosePiecePart) return

      // Get current choice and expected choice
      const nosePieceChoice =
        this.state.choices[nosePiecePart.id] || this.state.secondaryChoices[nosePiecePart.id]
      const frameChoiceSuffix = this.frameChoice?.sku?.split('_').slice(1).join('_')
      const expectedNosePieceChoice = nosePiecePart.options.find((option) =>
        option.sku.endsWith(`_${frameChoiceSuffix}`)
      )

      // If the choice is already correct then return now
      if (expectedNosePieceChoice?.sku === nosePieceChoice) return

      // Update choice
      if (expectedNosePieceChoice?.sku) this.setChoice(expectedNosePieceChoice)
    }

    checkSwappableParts() {
      // Check that no swappable parts are double-defined (eg both nose piece and base frame have choices)
      ;(this.product.swappableParts || []).forEach(([partSKU1, partSKU2]) => {
        const part1 = this.product.parts.find((part) => part.stockManagedSKUPrefix === partSKU1)
        const part2 = this.product.parts.find((part) => part.stockManagedSKUPrefix === partSKU2)

        if (part1.id in this.state.choices && part2.id in this.state.choices) {
          // Both parts are defined in choices
          delete this.state.choices[part2.id]
        }

        if (part1.id in this.state.secondaryChoices && part2.id in this.state.secondaryChoices) {
          // Both parts are defined in secondaryChoices
          delete this.state.secondaryChoices[part2.id]
        }

        if (part1.id in this.state.choices && part1.id in this.state.secondaryChoices) {
          // partSKU1 is defined in both choices and secondaryChoices
          delete this.state.secondaryChoices[part1.id]
        }

        if (part2.id in this.state.choices && part2.id in this.state.secondaryChoices) {
          // partSKU2 is defined in both choices and secondaryChoices
          delete this.state.secondaryChoices[part2.id]
        }
      })
    }

    checkPartsMode() {
      // Check that we've only got one part selected if we're in parts or CK mode
      if (
        !['parts', 'conversionkit'].includes(this.product?.pageMode) ||
        Object.keys(this.state.choices) === 1
      )
        return

      this.state.choices = Object.fromEntries(
        Object.entries(this.state.choices)
          .filter(([_, option]) => option)
          .slice(0, 1)
      )
    }

    checkAllChoicesDefined() {
      // Check that all choices are defined and if not set a default choice
      const allPrimaryChoicesDefined = Object.values(this.state.choices).every((optionId) => optionId)
      const allSecondaryChoicesDefined = Object.values(this.state.secondaryChoices).every(
        (optionId) => optionId
      )

      if (allPrimaryChoicesDefined && allSecondaryChoicesDefined) {
        return
      }

      ;[...Object.keys(this.state.choices), ...Object.keys(this.state.secondaryChoices)].forEach((partId) => {
        if (this.state.choices[partId] || this.state.secondaryChoices[partId]) return

        this.setChoice(this.product.parts.find((part) => part.id === partId).options[0])
      })
    }

    setLimitedEditionDefaults() {
      // Handle limited editions with conversion kits
      if (this.product.frameTypeOverride === 'ck') {
        this.state.conversionKitEnabled = true
        this.state.frameTypeOverrideIndex = 0
      }
    }

    setActivePart(part) {
      // If this part is already in choices then no change is required
      if (part.id in this.state.choices) return

      const allChoices = {
        ...this.state.choices,
        ...this.state.secondaryChoices,
      }
      this.state.choices = {}
      this.state.secondaryChoices = {}
      Object.entries(allChoices).forEach(([partId, optionId]) => {
        if (partId === part.id) {
          this.state.choices[partId] = optionId
        } else {
          this.state.secondaryChoices[partId] = optionId
        }
      })
    }

    swapPrimaryAndSecondaryChoices(primaryPart, secondaryPart) {
      if (primaryPart.id in this.secondaryChoices) {
        ;[primaryPart, secondaryPart] = [secondaryPart, primaryPart]
      }
      this.state.choices[secondaryPart.id] = this.state.secondaryChoices[secondaryPart.id]
      this.state.secondaryChoices[primaryPart.id] = this.state.choices[primaryPart.id]
      delete this.state.choices[primaryPart.id]
      delete this.state.secondaryChoices[secondaryPart.id]
    }

    toggleConversionKit(enabled) {
      // Handle Limited Editions differently
      if (this.product.isLimitedEdition) {
        this.state.frameTypeOverrideIndex = this.state.frameTypeOverrideIndex === 0 ? 1 : 0
        return
      }

      if (enabled === undefined) {
        this.state.conversionKitEnabled = !this.state.conversionKitEnabled
      } else {
        this.state.conversionKitEnabled = enabled
      }

      // If conversion kit is enabled ensure that it has a design set
      const secondaryPart = this.secondarySwappablePart
      if (this.state.conversionKitEnabled && !(secondaryPart.id in this.state.secondaryChoices)) {
        this.state.secondaryChoices[secondaryPart.id] = secondaryPart.options[0].id
      }
    }

    setFrameType(frameType) {
      if (this.frameType === frameType) return
      this.swapPrimaryAndSecondaryChoices(this.secondarySwappablePart, this.primarySwappablePart)
      this.ensureChoiceValidity()
    }

    swapFrameType() {
      // Determine the alternative frame type
      const frameTypeDetails = this.frameTypes.find(
        (ft) => !ft.conversionKit && ft.frameType !== this.frameType
      )
      this.setFrameType(frameTypeDetails.frameType)
    }

    trackGetter(getter) {
      if (!this.getterCalls) this.getterCalls = {}
      this.getterCalls[getter] = (this.getterCalls[getter] || 0) + 1
    }

    setSelectedUpsellChoices(key, choice) {
      if (key && choice === null) {
        if (!(key in this.state.selectedUpsellChoices)) return

        delete this.state.selectedUpsellChoices[key]
        return
      }

      if (choice !== null) {
        this.state.selectedUpsellChoices = {
          ...this.state.selectedUpsellChoices,
          [key]: choice,
        }
      }
    }

    setLensTechSelectedState(lensTechSelected) {
      this.state.lensTechSelected = lensTechSelected
    }

    updateProductFromShopifyResponse(customisedProduct, freshStockResponse) {
      this.product.updateFromShopifyResponse(customisedProduct, freshStockResponse)
    }

    setProductCustomisedState(customised) {
      this.state.customised = customised
      if (customised) this.clearPrebuiltVariant()
    }

    clearPrebuiltVariant() {
      this.state.isPrebuiltVariant = false
      this.state.prebuiltVariant = null
    }
  }
}
